import React, { createContext, useContext, useState, useEffect } from 'react';

interface Notification {
  id: string;
  type: 'success' | 'error' | 'warning' | 'info' | 'governance' | 'racing' | 'treasury' | 'system' | 'security';
  title: string;
  message: string;
  timestamp: Date;
  read: boolean;
  priority: 'low' | 'medium' | 'high' | 'urgent';
  category: string;
  action?: {
    label: string;
    onClick: () => void;
    route?: string;
  };
  metadata?: {
    amount?: string;
    horseName?: string;
    proposalId?: string;
    transactionHash?: string;
  };
}

interface NotificationContextType {
  notifications: Notification[];
  unreadCount: number;
  addNotification: (notification: Omit<Notification, 'id' | 'timestamp' | 'read'>) => void;
  markAsRead: (id: string) => void;
  markAllAsRead: () => void;
  removeNotification: (id: string) => void;
  clearAll: () => void;
  getNotificationsByType: (type: string) => Notification[];
  getNotificationsByPriority: (priority: string) => Notification[];
  markCategoryAsRead: (category: string) => void;
  clearCategory: (category: string) => void;
  // Demo functions
  triggerDemoNotifications: () => void;
  simulateRacingWin: () => void;
  simulateGovernanceUpdate: () => void;
  simulateTreasuryUpdate: () => void;
  simulateSecurityAlert: () => void;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

// Helper function to get default notifications
const getDefaultNotifications = (): Notification[] => [
  {
    id: '1',
    type: 'racing',
    title: 'Thunder Bay Wins!',
    message: 'Thunder Bay has won the Keeneland Stakes, earning $45,000 for the syndicate.',
    timestamp: new Date(Date.now() - 2 * 60 * 60 * 1000),
    read: false,
    priority: 'high',
    category: 'racing',
    action: {
      label: 'View Details',
      onClick: () => console.log('View race details'),
      route: '/horses'
    },
    metadata: {
      horseName: 'Thunder Bay',
      amount: '$45,000'
    }
  },
  {
    id: '2',
    type: 'governance',
    title: 'New Proposal Available',
    message: 'A new governance proposal "Acquire Thunder Bay for $2.5M" is now open for voting.',
    timestamp: new Date(Date.now() - 4 * 60 * 60 * 1000),
    read: false,
    priority: 'medium',
    category: 'governance',
    action: {
      label: 'Vote Now',
      onClick: () => console.log('Navigate to governance'),
      route: '/governance'
    },
    metadata: {
      proposalId: 'prop-001'
    }
  },
  {
    id: '3',
    type: 'treasury',
    title: 'Monthly Yield Distributed',
    message: 'Your share of the monthly yield ($1,250.50 SUI) has been distributed to your wallet.',
    timestamp: new Date(Date.now() - 24 * 60 * 60 * 1000),
    read: true,
    priority: 'medium',
    category: 'treasury',
    metadata: {
      amount: '$1,250.50 SUI'
    }
  },
  {
    id: '4',
    type: 'success',
    title: 'Wallet Connected',
    message: 'Your wallet has been successfully connected to SUI-ndicate.',
    timestamp: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000),
    read: true,
    priority: 'low',
    category: 'system'
  }
];

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (context === undefined) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
};

export const NotificationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  
  // Load notifications from localStorage on initialization
  const [notifications, setNotifications] = useState<Notification[]>(() => {
    const saved = localStorage.getItem('sui-ndicate-notifications');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        return parsed.map((n: any) => ({
          ...n,
          timestamp: new Date(n.timestamp)
        }));
      } catch {
        return getDefaultNotifications();
      }
    }
    return getDefaultNotifications();
  });

  // Save notifications to localStorage whenever they change
  useEffect(() => {
    localStorage.setItem('sui-ndicate-notifications', JSON.stringify(notifications));
  }, [notifications]);

  const unreadCount = notifications.filter(n => !n.read).length;

  const addNotification = (notification: Omit<Notification, 'id' | 'timestamp' | 'read'>) => {
    const newNotification: Notification = {
      ...notification,
      id: Date.now().toString(),
      timestamp: new Date(),
      read: false
    };
    setNotifications(prev => [newNotification, ...prev]);
  };

  const markAsRead = (id: string) => {
    setNotifications(prev => 
      prev.map(n => n.id === id ? { ...n, read: true } : n)
    );
  };

  const markAllAsRead = () => {
    setNotifications(prev => 
      prev.map(n => ({ ...n, read: true }))
    );
  };

  const removeNotification = (id: string) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
  };

  const clearAll = () => {
    setNotifications([]);
  };

  const getNotificationsByType = (type: string) => {
    return notifications.filter(n => n.type === type);
  };

  const getNotificationsByPriority = (priority: string) => {
    return notifications.filter(n => n.priority === priority);
  };

  const markCategoryAsRead = (category: string) => {
    setNotifications(prev => 
      prev.map(n => n.category === category ? { ...n, read: true } : n)
    );
  };

  const clearCategory = (category: string) => {
    setNotifications(prev => prev.filter(n => n.category !== category));
  };

  // Demo functions for investor presentations
  const triggerDemoNotifications = () => {
    const demoNotifications = [
      {
        type: 'racing' as const,
        title: '🏆 MAJOR WIN!',
        message: 'Thunder Bay just won the Kentucky Derby! Prize: $1.8M',
        priority: 'urgent' as const,
        category: 'racing',
        action: {
          label: 'View Race',
          onClick: () => window.location.href = '/horses',
          route: '/horses'
        },
        metadata: {
          horseName: 'Thunder Bay',
          amount: '$1,800,000'
        }
      },
      {
        type: 'treasury' as const,
        title: '💰 Treasury Milestone',
        message: 'Syndicate treasury has reached $10M SUI!',
        priority: 'high' as const,
        category: 'treasury',
        metadata: {
          amount: '$10,000,000 SUI'
        }
      },
      {
        type: 'governance' as const,
        title: '🗳️ Critical Vote',
        message: 'Proposal to acquire 3 new champion horses needs your vote!',
        priority: 'high' as const,
        category: 'governance',
        action: {
          label: 'Vote Now',
          onClick: () => window.location.href = '/governance',
          route: '/governance'
        }
      },
      {
        type: 'security' as const,
        title: '🔒 Security Alert',
        message: 'Multi-sig transaction requires your approval for $2.5M acquisition.',
        priority: 'urgent' as const,
        category: 'security',
        action: {
          label: 'Review',
          onClick: () => console.log('Review security alert')
        }
      }
    ];

    demoNotifications.forEach((notification, index) => {
      setTimeout(() => {
        addNotification(notification);
      }, index * 2000); // Stagger notifications
    });
  };

  const simulateRacingWin = () => {
    const horses = ['Thunder Bay', 'Golden Storm', 'Lightning Strike', 'Royal Victory'];
    const races = ['Kentucky Derby', 'Preakness Stakes', 'Belmont Stakes', 'Breeders Cup'];
    const horse = horses[Math.floor(Math.random() * horses.length)];
    const race = races[Math.floor(Math.random() * races.length)];
    const prize = (Math.random() * 2000000 + 500000).toFixed(0);

    addNotification({
      type: 'racing',
      title: `🏆 ${horse} Wins!`,
      message: `${horse} has won the ${race}, earning $${prize} for the syndicate!`,
      priority: 'high',
      category: 'racing',
      action: {
        label: 'View Details',
        onClick: () => window.location.href = '/horses',
        route: '/horses'
      },
      metadata: {
        horseName: horse,
        amount: `$${prize}`
      }
    });
  };

  const simulateGovernanceUpdate = () => {
    const proposals = [
      'Acquire Thunder Bay for $2.5M',
      'Increase staking rewards to 18%',
      'Purchase 2 new champion horses',
      'Distribute quarterly dividends'
    ];
    const proposal = proposals[Math.floor(Math.random() * proposals.length)];

    addNotification({
      type: 'governance',
      title: '🗳️ New Proposal',
      message: `"${proposal}" is now open for voting. Voting ends in 48 hours.`,
      priority: 'medium',
      category: 'governance',
      action: {
        label: 'Vote Now',
        onClick: () => window.location.href = '/governance',
        route: '/governance'
      }
    });
  };

  const simulateTreasuryUpdate = () => {
    const amount = (Math.random() * 1000000 + 500000).toFixed(0);
    const change = Math.random() > 0.5 ? 'increased' : 'decreased';
    
    addNotification({
      type: 'treasury',
      title: '💰 Treasury Update',
      message: `Treasury value has ${change} by $${amount} this week.`,
      priority: 'medium',
      category: 'treasury',
      metadata: {
        amount: `$${amount}`
      }
    });
  };

  const simulateSecurityAlert = () => {
    addNotification({
      type: 'security',
      title: '🔒 Security Alert',
      message: 'Multi-signature transaction requires your approval for treasury management.',
      priority: 'urgent',
      category: 'security',
      action: {
        label: 'Review Transaction',
        onClick: () => console.log('Review security transaction')
      }
    });
  };

  // Simulate real-time notifications (reduced frequency for demo)
  useEffect(() => {
    const interval = setInterval(() => {
      // Only add notifications occasionally (5% chance every 60 seconds)
      if (Math.random() < 0.05) {
        const notificationTypes = [
          {
            type: 'treasury' as const,
            title: 'Treasury Update',
            message: 'Treasury value has increased by $125,000 this week.',
            priority: 'low' as const,
            category: 'treasury'
          },
          {
            type: 'racing' as const,
            title: 'Training Update',
            message: 'Golden Storm completed training session with excellent results.',
            priority: 'low' as const,
            category: 'racing'
          },
          {
            type: 'governance' as const,
            title: 'Voting Reminder',
            message: 'Don\'t forget to vote on the current governance proposals.',
            priority: 'medium' as const,
            category: 'governance'
          }
        ];

        const randomNotification = notificationTypes[Math.floor(Math.random() * notificationTypes.length)];
        addNotification(randomNotification);
      }
    }, 60000); // Reduced to every 60 seconds

    return () => clearInterval(interval);
  }, []);

  return (
    <NotificationContext.Provider value={{
      notifications,
      unreadCount,
      addNotification,
      markAsRead,
      markAllAsRead,
      removeNotification,
      clearAll,
      getNotificationsByType,
      getNotificationsByPriority,
      markCategoryAsRead,
      clearCategory,
      triggerDemoNotifications,
      simulateRacingWin,
      simulateGovernanceUpdate,
      simulateTreasuryUpdate,
      simulateSecurityAlert
    }}>
      {children}
    </NotificationContext.Provider>
  );
};
